import { useMemo } from "react";

import { getFinalDate } from "@/features/assignment";
import { AssessmentFragment, WorkSettingsFragment } from "@/generated/graphql";

export interface WorkDueDate {
  dueDate: string | null;
  /**
   * Pre-extension due date, for the extension pill's tooltip. `null` for
   * Peer Evaluation (never extended) or when the work has no due date set.
   */
  originalDueDate: string | null;
  isExtended: boolean;
}

export interface WorkDueDates {
  group: WorkDueDate;
  individual: WorkDueDate;
  peer: WorkDueDate;
}

/**
 * Due-date derivation shared by `InstructionsRailCard` and `InstructionCard`:
 * the sheet's date vs. the work's own extension (`getFinalDate`), whichever
 * is later. Peer Evaluation is never extended, so its date comes from the
 * assessment alone.
 */
export function useWorkDueDates(
  assessment: AssessmentFragment,
  groupWorkSettings: WorkSettingsFragment | null | undefined,
  individualWorkSettings: WorkSettingsFragment | null | undefined
): WorkDueDates {
  return useMemo(() => {
    const [groupDueDate, groupIsExtended] = getFinalDate({
      settings: assessment.settings ?? {},
      workSettings: groupWorkSettings ?? null,
      isGroupWork: true,
    });

    const [individualDueDate, individualIsExtended] = getFinalDate({
      settings: assessment.settings ?? {},
      workSettings: individualWorkSettings ?? null,
    });

    return {
      group: {
        dueDate: groupDueDate,
        originalDueDate: assessment.settings?.dueDate ?? null,
        isExtended: groupIsExtended,
      },
      individual: {
        dueDate: individualDueDate,
        originalDueDate:
          assessment.settings?.individualReportDueDate ??
          assessment.settings?.dueDate ??
          null,
        isExtended: individualIsExtended,
      },
      peer: {
        dueDate: assessment.settings?.peerEvaluationDueDate ?? null,
        originalDueDate: null,
        isExtended: false,
      },
    };
  }, [assessment, groupWorkSettings, individualWorkSettings]);
}
